<template>
  <section class="Grid-layout">
    <div class="Grid-twelve Grid-twelve--subgrid Grid-twelve--restaurant">
      <ux-atoms-typo :text="personalizedPage?.title" as="h1" class="mt-10" color="dark" text-align="center" variant="expressive-heading-01" />
      <div class="mt-11 mb-12">
        <template :key="`cms-richtext-${index}`" v-for="(component, index) in components">
          <component v-bind="component" :is="getComponent(component.nodeType)" v-if="component.elements" />
        </template>
      </div>
    </div>
  </section>
</template>
<script lang="ts" setup>
import CmsRichTextList from '~/components/ux/atoms/RichText/CmsRichTextList/CmsRichTextList.vue';
import CmsRichTextText from '~/components/ux/atoms/RichText/CmsRichTextText/CmsRichTextText.vue';
import { RichTextComponentType } from '~/components/ux/atoms/RichText/types';
import { usePersonalizedPageAsyncData } from '~/graphql';
import { gtmPageAndUser, gtmUser } from '~/helpers/gtm';

const route = useRoute();
const setI18nParams = useSetI18nParams();
const { locale, t } = useI18n();
const userStore = useUserStore();

const slug = computed(() => (route.params.slug ? (route.params.slug as string) : ''));

const { data, error } = await usePersonalizedPageAsyncData({ slug: slug.value });

useAsyncDataError(error, 'page-slug');

useRedirectIfEmpty(data.value?.personalizedPage);

const personalizedPage = computed(() => data.value?.personalizedPage);

const components = computed(() => {
  if (!personalizedPage.value || !personalizedPage.value.content) {
    return [];
  }
  return usePersonalizedPage(personalizedPage.value.content);
});

const getComponent = (nodeType: RichTextComponentType) => {
  switch (nodeType) {
    case RichTextComponentType.Text:
      return CmsRichTextText;
    case RichTextComponentType.List:
      return CmsRichTextList;
    default:
      return null;
  }
};

if (personalizedPage.value?.translatedSlug) {
  setI18nParams({
    en: { slug: personalizedPage.value.translatedSlug.en },
    fr: { slug: personalizedPage.value.translatedSlug.fr }
  });
}

const pageEvent = () => {
  if (route.path === `/${locale.value}${t('ux.molecules.bookingConfirmation.termsOfUseLink')}`) {
    gtmPageAndUser({
      brandcontext: 'restaurantsandbars',
      category: 'restaurantsandbars',
      countrymarket: 'fr',
      currencycode: 'EUR',
      dynamic_datas_ready: true,
      event: 'push_dynamic_datas',
      pagename: 'restaurantsandbars::content::legal',
      pagetype: 'restaurantsandbars',
      sub_category_level1: 'content',
      sub_category_level2: 'legal'
    });
  } else {
    gtmUser();
  }
};

whenever(
  () => userStore.userLoadDone,
  () => {
    pageEvent();
  }
);

onMounted(() => {
  if (userStore.userLoadDone) {
    pageEvent();
  }
});

useHead(() => ({
  meta: [
    {
      content: personalizedPage.value?.metaDescription,
      name: 'description'
    }
  ],
  title: personalizedPage.value?.metaTitle ?? t('pages.personalizedPage.title', { pageName: personalizedPage.value?.title })
}));
</script>
